import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Alert,
  Card,
  CardContent,
  Grid,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Snackbar,
  Tabs,
  Tab
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Dashboard as DashboardIcon,
  TrendingUp as TrendingUpIcon,
  Business as BusinessIcon,
  Assessment as AssessmentIcon,
  Schedule as ScheduleIcon,
  DeleteSweep as ClearCacheIcon,
  Search as SearchIcon,
  Update as UpdateIcon
} from '@mui/icons-material';
import axios from 'axios';
import AgencyDataTable from './AgencyDataTable';
import AgencyFilters from './AgencyFilters';
import AgencyKeywordTable from './AgencyKeywordTable';

const AgencyDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);
  const [sortBy, setSortBy] = useState('businessName');
  const [sortOrder, setSortOrder] = useState('asc');
  const [pagination, setPagination] = useState({ page: 1, pageSize: 100 });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [currentTab, setCurrentTab] = useState(0);
  const [updatingData, setUpdatingData] = useState(false);

  // Update all business data from Google My Business
  const updateAllBusinessData = async () => {
    try {
      setUpdatingData(true);
      console.log('🔄 Starting business data update...');
      
      const response = await axios.post('/api/agency/update-all-business-data', {}, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });

      if (response.data.success) {
        setSnackbar({
          open: true,
          message: `Started updating data for ${response.data.businessCount} businesses. This will take ${response.data.estimatedDuration}.`,
          severity: 'success'
        });
        
        console.log('✅ Business data update started successfully');
        
        // Reload dashboard data after a delay
        setTimeout(() => {
          loadDashboardData();
        }, 5000);
        
      } else {
        throw new Error(response.data.error || 'Update failed');
      }
    } catch (error) {
      console.error('❌ Business data update failed:', error);
      setSnackbar({
        open: true,
        message: `Update failed: ${error.response?.data?.error || error.message}`,
        severity: 'error'
      });
    } finally {
      setUpdatingData(false);
    }
  };

  // Load dashboard data
  const loadDashboardData = async (showLoading = true, forceRefresh = false) => {
    try {
      if (showLoading) setLoading(true);

      // Check localStorage cache (4 hours TTL) if not forcing refresh
      if (!forceRefresh) {
        const cached = localStorage.getItem('agencyDashboardCache');
        const cachedTime = localStorage.getItem('agencyDashboardCacheTime');
        const FOUR_HOURS = 4 * 60 * 60 * 1000;

        if (cached && cachedTime && Date.now() - parseInt(cachedTime) < FOUR_HOURS) {
          setDashboardData(JSON.parse(cached));
          if (showLoading) setLoading(false);
          return;
        }
      } else {
        // Clear cache when forcing refresh
        localStorage.removeItem('agencyDashboardCache');
        localStorage.removeItem('agencyDashboardCacheTime');
      }

      const response = await axios.get('/api/agency-dashboard', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });

      if (response.data.success) {
        if (response.data.data) {
          setDashboardData(response.data.data);
          // Cache in localStorage for 4 hours
          localStorage.setItem('agencyDashboardCache', JSON.stringify(response.data.data));
          localStorage.setItem('agencyDashboardCacheTime', Date.now().toString());

          if (response.data.needsUpdate) {
            setSnackbar({
              open: true,
              message: 'Dashboard-Daten sind veraltet. Aktualisierung empfohlen.',
              severity: 'warning'
            });
          }
        } else {
          setDashboardData(null);
        }
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setSnackbar({
        open: true,
        message: 'Fehler beim Laden der Dashboard-Daten',
        severity: 'error'
      });
    } finally {
      if (showLoading) setLoading(false);
    }
  };


  // Clear cache
  const clearCache = async () => {
    try {
      // Clear client-side cache
      localStorage.removeItem('agencyDashboardCache');
      localStorage.removeItem('agencyDashboardCacheTime');

      const response = await axios.delete('/api/agency/dashboard/cache', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });

      if (response.data.success) {
        setDashboardData(null);
        setSnackbar({
          open: true,
          message: 'Cache erfolgreich geleert',
          severity: 'success'
        });
        // Reload data after clearing cache
        setTimeout(() => loadDashboardData(true, true), 500);
      }
    } catch (error) {
      console.error('Error clearing cache:', error);
      setSnackbar({
        open: true,
        message: 'Fehler beim Leeren des Cache',
        severity: 'error'
      });
    }
  };


  // Filter and sort data
  const filterAndSortData = useCallback(async () => {
    if (!dashboardData) return;

    try {
      console.log('🔍 Filtering data with:', { sortBy, sortOrder, page: pagination.page, pageSize: pagination.pageSize });
      
      const response = await axios.post('/api/agency/dashboard/filter', {
        filters,
        sortBy,
        sortOrder,
        page: pagination.page,
        pageSize: pagination.pageSize
      }, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });

      if (response.data.success) {
        console.log('✅ Filter response:', response.data.data);
        console.log('🎯 Sample customer keywordIndicators:', response.data.data.customers[0]?.keywordIndicators);
        return {
          customers: response.data.data.customers,
          pagination: response.data.data.pagination
        };
      }
    } catch (error) {
      console.error('❌ Error filtering data:', error);
      return {
        customers: dashboardData.customers || [],
        pagination: { total: 0, page: 1, pageSize: 100, totalPages: 0 }
      };
    }
  }, [dashboardData, filters, sortBy, sortOrder, pagination.page, pagination.pageSize]);

  // Load data on mount
  useEffect(() => {
    loadDashboardData();
  }, []);

  // Filter/sort data when parameters change
  const [filteredData, setFilteredData] = useState({ customers: [], pagination: {} });

  useEffect(() => {
    const applyFilters = async () => {
      if (dashboardData) {
        const result = await filterAndSortData();
        if (result) {
          setFilteredData(result);
        }
      }
    };
    
    applyFilters();
  }, [dashboardData, filterAndSortData]);

  const handleSortChange = (field, order) => {
    setSortBy(field);
    setSortOrder(order);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (page, pageSize) => {
    setPagination(prev => ({
      ...prev,
      page,
      ...(pageSize && { pageSize })
    }));
  };

  const formatLastAggregated = (date) => {
    if (!date) return 'Nie';
    const now = new Date();
    const diff = now - new Date(date);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours === 0) return `vor ${minutes} Minuten`;
    if (hours < 24) return `vor ${hours} Stunden`;
    const days = Math.floor(hours / 24);
    return `vor ${days} Tagen`;
  };

  return (
    <Container maxWidth={false} sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 3
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <DashboardIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
              Agentur-Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Übersicht aller Kundenaccounts und deren Performance
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {dashboardData && (
            <Chip
              label={`Letzte Aktualisierung: ${formatLastAggregated(dashboardData.lastAggregated)}`}
              size="small"
              color={dashboardData.needsUpdate ? 'warning' : 'default'}
              sx={{ mr: 1 }}
            />
          )}
          
          <Tooltip title="Cache leeren">
            <IconButton onClick={clearCache} color="secondary">
              <ClearCacheIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Daten neu laden">
            <IconButton onClick={() => loadDashboardData(true, true)} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<UpdateIcon />}
            onClick={updateAllBusinessData}
            disabled={updatingData}
            sx={{ mr: 2 }}
          >
            {updatingData ? 'Updating...' : 'Daten aktualisieren'}
          </Button>
        </Box>
      </Box>

      {/* No Data State */}
      {!loading && !dashboardData && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Noch keine Daten verfügbar
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Keine Dashboard-Daten verfügbar.
          </Typography>
        </Paper>
      )}

      {/* Dashboard Content */}
      {dashboardData && (
        <>
          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <BusinessIcon color="primary" sx={{ fontSize: 32, mb: 1 }} />
                  <Typography variant="h4" color="primary">
                    {dashboardData.stats?.totalCustomers || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Kunden gesamt
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <TrendingUpIcon color="success" sx={{ fontSize: 32, mb: 1 }} />
                  <Typography variant="h4" color="success.main">
                    {dashboardData.stats?.customersWithKeywords || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Mit Keywords
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <ScheduleIcon color="info" sx={{ fontSize: 32, mb: 1 }} />
                  <Typography variant="h4" color="info.main">
                    {dashboardData.stats?.customersWithPosts || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Mit Posts
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <AssessmentIcon 
                    sx={{ 
                      fontSize: 32, 
                      mb: 1,
                      color: (dashboardData.stats?.urgentActionNeeded || 0) > 0 ? 'error.main' : 'success.main'
                    }} 
                  />
                  <Typography 
                    variant="h4" 
                    color={(dashboardData.stats?.urgentActionNeeded || 0) > 0 ? 'error.main' : 'success.main'}
                  >
                    {dashboardData.stats?.urgentActionNeeded || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Handlungsbedarf
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Tab Navigation */}
          <Paper sx={{ mb: 3 }}>
            <Tabs 
              value={currentTab} 
              onChange={(e, newValue) => setCurrentTab(newValue)}
              sx={{ 
                borderBottom: 1, 
                borderColor: 'divider',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 500
                }
              }}
            >
              <Tab 
                icon={<BusinessIcon />} 
                label="Unternehmen" 
                iconPosition="start"
                sx={{ minHeight: 56 }}
              />
              <Tab 
                icon={<SearchIcon />} 
                label="Schlüsselwörter-Details" 
                iconPosition="start"
                sx={{ minHeight: 56 }}
              />
            </Tabs>
          </Paper>

          {currentTab === 0 && (
            <>
              {/* Filters */}
              <AgencyFilters
                filters={filters}
                onFiltersChange={setFilters}
                stats={dashboardData.stats}
                isCollapsed={filtersCollapsed}
                onToggleCollapse={() => setFiltersCollapsed(!filtersCollapsed)}
              />

              {/* Data Table */}
              <AgencyDataTable
                data={filteredData.customers}
                loading={loading}
                pagination={filteredData.pagination}
                onSortChange={handleSortChange}
                onPageChange={handlePageChange}
                sortBy={sortBy}
                sortOrder={sortOrder}
              />
            </>
          )}

          {currentTab === 1 && (
            <AgencyKeywordTable />
          )}
        </>
      )}


      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AgencyDashboard;